import { FC, useEffect, useRef, useState } from "react";
import {
  useGetUsersCommissionRatesQuery,
  useGetUsersQuery,
} from "../../api/rtkQueryApi/UsersApi";
import {
  EuiBasicTableColumn,
  EuiButton,
  EuiButtonIcon,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFlyout,
  EuiFlyoutBody,
  EuiFlyoutHeader,
  EuiFormRow,
  EuiInMemoryTable,
  EuiPanel,
  EuiSpacer,
  EuiStat,
  EuiTitle,
} from "@elastic/eui";
import { NumberToCurrency } from "../../utils/Formatters";
import { DateRangePicker } from "../../components/time/DateRangePicker";
import moment, { Moment } from "moment/moment";
import { useGetGrowthCoachPlansQuery } from "../../api/rtkQueryApi/PlansApi";
import { SalesSummary } from "../../components/commissions/SalesSummary";
import {
  useCommissionsGetAllQuery,
  useCommissionsGetBySalesEmailQuery,
} from "../../api/rtkQueryApi/CommissionsApi";
import { DownloadAsCsv } from "../../utils/DownloadAsCsv";

export interface UserCommissionData {
  firstName: string;
  lastName: string;
  email: string;
  totalNcr: string;
  commissionRate: string;
  commissionDue: string;
}

export const AccountingCommissionsSummary: FC = () => {
  const [startDate, setStartDate] = useState<Moment>(moment().startOf("month"));
  const [endDate, setEndDate] = useState<Moment>(moment().endOf("month"));
  const [minDate] = useState<Moment>();
  const [maxDate] = useState<Moment>();
  const [totalCommission, setTotalCommission] = useState(0);

  const [selectedUser, setSelectedUser] = useState<UserCommissionData>();
  const [showUserDetails, setShowUserDetails] = useState<boolean>(false);

  const allCommissionQuery = useCommissionsGetAllQuery();

  const growthCoachPlansQuery = useGetGrowthCoachPlansQuery(
    { email: selectedUser?.email ?? "" },
    { skip: !selectedUser },
  );

  const now = useRef(moment());

  const allCommissionRates = useGetUsersCommissionRatesQuery({
    forMonth: now.current,
  });

  const growthCoachCommissionsQuery = useCommissionsGetBySalesEmailQuery(
    { salesEmail: selectedUser?.email ?? "" },
    { skip: !selectedUser },
  );

  const usersQuery = useGetUsersQuery();
  const [filteredUsers, setFilteredUsers] = useState<UserCommissionData[]>([]);

  useEffect(() => {
    if (usersQuery.data && allCommissionQuery.data && allCommissionRates.data) {
      const users = usersQuery.data;
      const commissions = allCommissionQuery.data.filter((c) =>
        moment(c.invoicePaidDate).isBetween(startDate, endDate),
      );

      setTotalCommission(
        commissions.reduce(
          (acc, commission) => acc + commission.commissionAmount,
          0,
        ),
      );

      const commissionData: UserCommissionData[] = [];

      users.forEach((user) => {
        const userCommissions = commissions.filter(
          (c) => c.salesAgentId === user.email,
        );

        const totalNcr = userCommissions.reduce(
          (acc, commission) => acc + commission.netCommissionableRevenue,
          0,
        );

        const commissionRate =
          allCommissionRates.data?.find((rate) => rate.email === user.email)
            ?.commissionRates[0].commissionRate ?? 0;

        commissionData.push({
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          totalNcr: NumberToCurrency(totalNcr),
          commissionRate:
            commissionRate !== 0 ? `${commissionRate * 100}%` : "N/A",
          commissionDue: NumberToCurrency(totalNcr * commissionRate),
        });
      });

      setFilteredUsers(commissionData);
    }
  }, [
    usersQuery.data,
    allCommissionQuery.data,
    allCommissionRates.data,
    startDate,
    endDate,
  ]);

  const onDateRangeChange = (startDate: Moment, endDate: Moment) => {
    setStartDate(startDate);
    setEndDate(endDate);
  };

  const onViewUserDetails = (user: UserCommissionData) => {
    setSelectedUser(user);
    setShowUserDetails(true);
  };

  const onCloseUserDetails = () => {
    setSelectedUser(undefined);
    setShowUserDetails(false);
  };

  const userColumns: EuiBasicTableColumn<UserCommissionData>[] = [
    {
      name: "Growth Coach",
      render: (user: UserCommissionData) =>
        `${user.firstName} ${user.lastName}`,
      sortable: (user: UserCommissionData) =>
        `${user.firstName} ${user.lastName}`,
    },
    {
      field: "totalNcr",
      name: "Total NCR",
      sortable: true,
    },
    {
      field: "commissionRate",
      name: "Commission Rate For Month",
      sortable: true,
    },
    {
      field: "commissionDue",
      name: "Commission Due",
      sortable: true,
    },
    {
      render: (user: UserCommissionData) => (
        <EuiFlexGroup>
          <EuiFlexItem grow={false}>
            <EuiButtonIcon
              iconType={"eye"}
              onClick={() => onViewUserDetails(user)}
            />
          </EuiFlexItem>
        </EuiFlexGroup>
      ),
    },
  ];

  const pagination = {
    initialPageSize: 10,
    pageSizeOptions: [10, 25, 50],
  };

  return (
    <>
      <EuiPanel hasShadow={false} hasBorder={true} style={{ width: 240 }}>
        <EuiStat
          description={`Total Commission`}
          title={NumberToCurrency(totalCommission)}
          titleSize={"s"}
        />
      </EuiPanel>
      <EuiSpacer />
      <EuiFlexGroup direction={"row"} justifyContent={"spaceBetween"}>
        <EuiFormRow label={"Date Range"}>
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onChange={onDateRangeChange}
            maxDate={maxDate}
            minDate={minDate}
          />
        </EuiFormRow>
        <EuiButton
          onClick={() =>
            DownloadAsCsv(filteredUsers, "commissions-summary.csv")
          }
          disabled={filteredUsers.length === 0}
        >
          Download as CSV
        </EuiButton>
      </EuiFlexGroup>
      <EuiSpacer />
      <EuiFormRow fullWidth label={"Users"}>
        <EuiInMemoryTable
          items={filteredUsers}
          itemId="id"
          columns={userColumns}
          pagination={pagination}
          sorting={true}
        />
      </EuiFormRow>
      {showUserDetails && selectedUser && (
        <EuiFlyout ownFocus onClose={onCloseUserDetails} size={"l"}>
          <EuiFlyoutHeader hasBorder>
            <EuiTitle size={"m"}>
              <h2>
                {selectedUser.firstName} {selectedUser.lastName} (
                {selectedUser.email})
              </h2>
            </EuiTitle>
          </EuiFlyoutHeader>
          <EuiFlyoutBody>
            <SalesSummary
              plans={growthCoachPlansQuery.data ?? []}
              commissions={growthCoachCommissionsQuery?.data ?? []}
              selectedUser={selectedUser}
              isLoading={
                growthCoachCommissionsQuery.isLoading ||
                growthCoachPlansQuery.isLoading ||
                growthCoachCommissionsQuery.isFetching ||
                growthCoachPlansQuery.isFetching
              }
            />
          </EuiFlyoutBody>
        </EuiFlyout>
      )}
    </>
  );
};
